import React, { useState } from 'react';
import { FiMail, FiEye, FiEyeOff, FiExternalLink, FiCopy, FiCheck, FiPlus, FiTrash2 } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

interface EmailCredential {
  email: string;
  password: string;
  label: string;
}

const defaultCredentials: EmailCredential[] = [
  { email: 'admin@snap-chart.ai', password: 'quantumfintech09439AA!!', label: 'Admin' },
  { email: 'support@snap-chart.ai', password: 'quantumfintech09439AA!', label: 'Support' },
  { email: 'sales@snap-chart.ai', password: 'quantumfintech09439AA', label: 'Sales' },
  { email: 'media@snap-chart.ai', password: 'quantumfintech09439AA!!!!', label: 'Media' },
  { email: 'partnerships@snap-chart.ai', password: 'quantumfintech09439AA!!!!!', label: 'Partnerships' },
  { email: 'notification@snap-chart.ai', password: 'quantumfintech09439AA!!!!!!!', label: 'Notification' },
  { email: 'privacy@snap-chart.ai', password: 'quantumfintech09439AA!!!!!!', label: 'Privacy' },
];

const STORAGE_KEY = 'snap-chart-extra-credentials';

const loadExtraCredentials = (): EmailCredential[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
};

const saveExtraCredentials = (creds: EmailCredential[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(creds));
};

const WEBMAIL_URL = 'https://privateemail.com/';

export const QuickAccessCredentials: React.FC = () => {
  const [extraCredentials, setExtraCredentials] = useState<EmailCredential[]>(loadExtraCredentials);
  const [visiblePasswords, setVisiblePasswords] = useState<Record<string, boolean>>({});
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const emailCredentials = [...defaultCredentials, ...extraCredentials];

  const addCredential = () => {
    if (!newEmail || !newPassword || !newLabel) return;
    const updated = [...extraCredentials, { email: newEmail, password: newPassword, label: newLabel }];
    setExtraCredentials(updated);
    saveExtraCredentials(updated);
    setNewLabel(''); setNewEmail(''); setNewPassword('');
    setShowAddForm(false);
  };

  const removeCredential = (email: string) => {
    const updated = extraCredentials.filter(c => c.email !== email);
    setExtraCredentials(updated);
    saveExtraCredentials(updated);
  };

  const isExtra = (email: string) => extraCredentials.some(c => c.email === email);

  const togglePassword = (email: string) => {
    setVisiblePasswords(prev => ({ ...prev, [email]: !prev[email] }));
  };

  const copyToClipboard = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header with Webmail Link */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Email Credentials</h2>
          <p className="text-gray-400 text-sm mt-1">Quick access to company email accounts</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white font-medium transition-colors"
          >
            <FiPlus />
            Add
          </button>
          <a
            href={WEBMAIL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-cyan-600 rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
          >
          <FiExternalLink />
          Open Webmail
        </a>
        </div>
      </div>

      {/* Add Credential Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-gray-900/80 border border-cyan-500/30 rounded-xl p-4 space-y-3"
          >
            <div className="grid grid-cols-3 gap-3">
              <input
                type="text"
                placeholder="Label (e.g. Marketing)"
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:border-cyan-500 outline-none"
              />
              <input
                type="email"
                placeholder="Email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:border-cyan-500 outline-none"
              />
              <input
                type="text"
                placeholder="Password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:border-cyan-500 outline-none"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowAddForm(false)} className="px-4 py-2 text-gray-400 hover:text-white text-sm transition-colors">
                Cancel
              </button>
              <button
                onClick={addCredential}
                disabled={!newLabel || !newEmail || !newPassword}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-40 rounded-lg text-white text-sm font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Credentials Grid */}
      <div className="grid gap-4">
        {emailCredentials.map((cred, index) => (
          <motion.div
            key={cred.email}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-gray-900/60 border border-purple-500/20 rounded-xl p-4 hover:border-cyan-500/30 transition-colors"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-600/20 to-cyan-600/20 rounded-lg flex items-center justify-center">
                  <FiMail className="text-cyan-400" />
                </div>
                <div>
                  <span className="text-xs font-medium text-purple-400 uppercase tracking-wider">{cred.label}</span>
                  <p className="text-white font-medium">{cred.email}</p>
                </div>
              </div>
              {isExtra(cred.email) && (
                <button
                  onClick={() => removeCredential(cred.email)}
                  className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                  title="Delete"
                >
                  <FiTrash2 size={16} />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => copyToClipboard(cred.email, `email-${cred.email}`)}
                className="flex-1 flex items-center justify-between px-3 py-2 bg-gray-800/50 rounded-lg border border-gray-700/50 hover:border-cyan-500/30 transition-colors"
              >
                <span className="text-gray-300 text-sm truncate">{cred.email}</span>
                {copiedField === `email-${cred.email}` ? (
                  <FiCheck className="text-green-400 flex-shrink-0" />
                ) : (
                  <FiCopy className="text-gray-500 flex-shrink-0" />
                )}
              </button>

              <button
                onClick={() => copyToClipboard(cred.password, `pwd-${cred.email}`)}
                className="flex-1 flex items-center justify-between px-3 py-2 bg-gray-800/50 rounded-lg border border-gray-700/50 hover:border-cyan-500/30 transition-colors"
              >
                <span className="text-gray-300 text-sm font-mono">
                  {visiblePasswords[cred.email] ? cred.password : '••••••••••••'}
                </span>
                {copiedField === `pwd-${cred.email}` ? (
                  <FiCheck className="text-green-400 flex-shrink-0" />
                ) : (
                  <FiCopy className="text-gray-500 flex-shrink-0" />
                )}
              </button>

              <button
                onClick={() => togglePassword(cred.email)}
                className="p-2 bg-gray-800/50 rounded-lg border border-gray-700/50 hover:border-purple-500/30 transition-colors"
              >
                {visiblePasswords[cred.email] ? (
                  <FiEyeOff className="text-gray-400" />
                ) : (
                  <FiEye className="text-gray-400" />
                )}
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Security Notice */}
      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
        <p className="text-yellow-400 text-sm">
          ⚠️ This section is only visible to super admins. Never share these credentials.
        </p>
      </div>
    </div>
  );
};

export default QuickAccessCredentials;
